// =============================================================
//  ManagePaymentsScreen — Gestion des paiements (achats + annonces)
//  Tabs, recherche, KPIs revenus, table des transactions.
// =============================================================

import React, { useEffect, useMemo, useState } from 'react';
import { View, Text, StyleSheet } from 'react-native';
import { collection, query, getDocs, orderBy } from 'firebase/firestore';
import { db } from '../private/firebaseConfig';

import AdminLayout from './web-ui/AdminLayout';
import {
  Card,
  Button,
  Badge,
  EmptyState,
  Loading,
  DataRow,
  SegmentedSelect,
  SearchBar,
  StatCard,
} from './web-ui/kit';
import { colors, spacing } from './web-ui/theme';

const FCFA = (n) => new Intl.NumberFormat('fr-FR').format(Number(n) || 0) + ' FCFA';

const TABS = [
  { value: 'purchases', label: 'Achats' },
  { value: 'announcements', label: 'Annonces' },
];

const toDate = (ts) => {
  if (!ts) return null;
  if (ts.toDate) return ts.toDate();
  if (typeof ts === 'number') return new Date(ts);
  return null;
};

const fmtDate = (ts) => {
  const d = toDate(ts);
  if (!d) return '—';
  return d.toLocaleDateString('fr-FR') + ' ' + d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
};

export default function ManagePaymentsScreen({ navigation }) {
  const [activeTab, setActiveTab] = useState('purchases');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [purchases, setPurchases] = useState([]);
  const [announcementPayments, setAnnouncementPayments] = useState([]);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [purchasesSnap, annSnap] = await Promise.all([
        getDocs(query(collection(db, 'purchases'), orderBy('purchaseDate', 'desc'))).catch(() =>
          getDocs(collection(db, 'purchases'))
        ),
        getDocs(query(collection(db, 'announcement-payments'), orderBy('createdAt', 'desc'))).catch(
          () => ({ docs: [] })
        ),
      ]);
      setPurchases(purchasesSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setAnnouncementPayments(annSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
    } catch (e) {
      console.log('payments load error', e?.message || e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
  }, []);

  const items = activeTab === 'purchases' ? purchases : announcementPayments;

  const stats = useMemo(() => {
    const total = items.length;
    const revenue = items.reduce(
      (s, p) => s + (Number(p.amount) || Number(p.prix) || 0),
      0
    );
    return { total, revenue };
  }, [items]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter((p) => {
      const fields = [
        p.documentTitle,
        p.titre,
        p.userName,
        p.userEmail,
        p.email,
        p.paymentReference,
        p.operator,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return fields.includes(q);
    });
  }, [items, search]);

  const statusVariant = (s) => {
    const v = String(s || '').toLowerCase();
    if (['successful', 'success', 'completed', 'paid'].includes(v)) return 'success';
    if (['pending', 'processing'].includes(v)) return 'warning';
    if (['failed', 'cancelled', 'expired'].includes(v)) return 'danger';
    return 'neutral';
  };

  return (
    <AdminLayout
      title="Gestion des paiements"
      subtitle="Achats utilisateurs et paiements d'annonces"
    >
      <View style={styles.statsRow}>
        <StatCard
          label="Transactions"
          value={stats.total}
          icon="credit-card-outline"
          accent={colors.primary}
          loading={loading}
        />
        <StatCard
          label="Revenus"
          value={loading ? '—' : FCFA(stats.revenue)}
          icon="cash-multiple"
          accent={colors.success}
          loading={loading}
        />
      </View>

      <Card style={{ marginBottom: spacing.lg }}>
        <SegmentedSelect options={TABS} value={activeTab} onChange={setActiveTab} />
      </Card>

      <View style={styles.toolbar}>
        <View style={{ flex: 1, minWidth: 240 }}>
          <SearchBar
            value={search}
            onChangeText={setSearch}
            placeholder="Rechercher (doc, email, référence)..."
          />
        </View>
      </View>

      <Card noPadding>
        {loading ? (
          <Loading label="Chargement..." />
        ) : filtered.length === 0 ? (
          <EmptyState
            icon="credit-card-off-outline"
            title="Aucune transaction"
            subtitle="Aucun paiement ne correspond à tes critères"
          />
        ) : (
          filtered.map((p, i) => {
            const title =
              p.documentTitle || p.titre || p.announcementTitle || 'Transaction';
            const sub =
              p.userName || p.userEmail || p.email || p.paymentReference || '—';
            const amount = Number(p.amount) || Number(p.prix) || 0;
            return (
              <DataRow
                key={p.id}
                last={i === filtered.length - 1}
                onPress={() => navigation.navigate('PaymentDetailScreen', { payment: p })}
              >
                <View
                  style={{
                    width: 40,
                    height: 40,
                    borderRadius: 10,
                    backgroundColor: colors.successSoft,
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Text style={{ color: colors.success, fontWeight: '700', fontSize: 14 }}>
                    {String(p.operator || 'M').charAt(0).toUpperCase()}
                  </Text>
                </View>
                <View style={{ flex: 1, minWidth: 0 }}>
                  <Text style={styles.rowTitle} numberOfLines={1}>
                    {title}
                  </Text>
                  <Text style={styles.rowSubtitle} numberOfLines={1}>
                    {sub} · {fmtDate(p.purchaseDate || p.createdAt)}
                  </Text>
                </View>
                <Text style={styles.amount}>{FCFA(amount)}</Text>
                <Badge variant={statusVariant(p.status)}>{p.status || 'paid'}</Badge>
                <Button
                  size="sm"
                  variant="ghost"
                  icon="arrow-right"
                  onPress={() => navigation.navigate('PaymentDetailScreen', { payment: p })}
                />
              </DataRow>
            );
          })
        )}
      </Card>
    </AdminLayout>
  );
}

const styles = StyleSheet.create({
  statsRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.lg,
    marginBottom: spacing.lg,
  },
  toolbar: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.md,
    marginBottom: spacing.lg,
  },
  rowTitle: { fontSize: 14, fontWeight: '600', color: colors.text },
  rowSubtitle: { fontSize: 12, color: colors.textSecondary, marginTop: 2 },
  amount: { fontSize: 14, fontWeight: '700', color: colors.text },
});
